import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { SearchBar } from '../components/ui/SearchBar';
import { Pagination } from '../components/ui/Pagination';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
  TableHeader,
} from '../components/ui/DataTable';
import { useDeleteKnowledgeBase, useKnowledgeBases } from '../hooks/useKnowledge';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';
import { useListState } from '../hooks/useListState';
import { useFlag } from '../hooks/useFeatureFlags';

/**
 * Knowledge bases — the groupings that decide scope (task 574).
 *
 * Deliberately thin: no upload lives here. A base is a name over a set of
 * documents from the account library, and the unit an agent is given.
 */
export default function KnowledgeBasesPage() {
  const navigate = useNavigate();
  // Deleting a base was reachable only from the base's own page (task 864); the
  // list is where a User tidying several of them is standing.
  const deleteBase = useDeleteKnowledgeBase();
  const canCreate = useFlag('innovate.fe.knowledge.bases.create');
  const documentsPageEnabled = useFlag('innovate.fe.knowledge.documents.page');

  const { page, limit, search, setPage, setLimit, setSearch } = useListState({
    entityType: 'knowledge-bases',
  });

  const { data, isLoading, error } = useKnowledgeBases(page, limit, {
    search: search || undefined,
  });
  const bases = data?.data ?? [];
  const pagination = data?.pagination;

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">
          Error loading knowledge bases. Please try again.
        </div>
      </div>
    );
  }

  const actions = canCreate ? (
    <Link
      to="/knowledge-bases/new"
      data-testid="knowledge-bases-new"
      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 hover:bg-indigo-700 dark:hover:bg-indigo-800"
    >
      <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
      New Knowledge Base
    </Link>
  ) : undefined;

  return (
    <div>
      <PageHeader
        title="Knowledge Bases"
        description={
          <>
            A knowledge base groups documents so an agent can be given exactly the material it
            should draw on. Documents themselves live in{' '}
            {documentsPageEnabled ? (
              <Link
                to="/knowledge-documents"
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
              >
                Documents
              </Link>
            ) : (
              'the account library'
            )}
            .
          </>
        }
        actions={actions}
      />

      <div className="mt-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <SearchBar value={search} onChange={setSearch} placeholder="Search knowledge bases..." />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading knowledge bases...</div>
          </div>
        ) : bases.length === 0 ? (
          <div className="text-center py-12">
            <RectangleStackIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="text-gray-500 dark:text-gray-400 mb-4 mt-4">
              {search
                ? `No knowledge bases found matching "${search}"`
                : 'No knowledge bases yet. Create one to group documents for your agents.'}
            </div>
            {!search && canCreate && (
              <Link
                to="/knowledge-bases/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                Create your first knowledge base
              </Link>
            )}
          </div>
        ) : (
          <DataTable>
            <DataTableHead>
              <TableHeader className="w-1/2">Knowledge base</TableHeader>
              <TableHeader className="w-32">Documents</TableHeader>
              <TableHeader className="w-32">Updated</TableHeader>
              {/* Unlabelled: a header over a delete control would read as a
                  fourth fact about the knowledge base. */}
              <TableHeader className="w-12">
                <span className="sr-only">Actions</span>
              </TableHeader>
            </DataTableHead>
            <DataTableBody>
              {bases.map((base) => (
                <DataTableRow key={base.id} onClick={() => navigate(`/knowledge-bases/${base.id}`)}>
                  <DataTableCell>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {base.name}
                      </div>
                      {base.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {base.description}
                        </div>
                      )}
                    </div>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {base.documentCount ?? '—'}
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(base.updatedAt).toLocaleDateString()}
                  </DataTableCell>
                  <DataTableCell className="w-12 whitespace-nowrap text-right">
                    <DeleteResourceControl
                      flag="innovate.fe.knowledge.bases.delete"
                      entity="knowledge-bases"
                      entityLabel="knowledge base"
                      name={base.name}
                      id={base.id}
                      stopPropagation
                      body={
                        <p>
                          This deletes the grouping only. Its documents stay in your library, along
                          with any other knowledge bases they belong to.
                        </p>
                      }
                      onDelete={() => deleteBase.mutateAsync(base.id)}
                    />
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="mt-6">
            <Pagination pagination={pagination} onPageChange={setPage} onLimitChange={setLimit} />
          </div>
        )}
      </div>
    </div>
  );
}
